import {
  isBrowserLackingCriticalFunctionality,
  isBrowserOld,
} from "./browserSupport";
import type { Config } from "./config";
import { hasStoredFlags } from "./config/feature-flag";

/**
 * The options passed in to the Universal Bootstrap
 */
export interface UniversalBootstrapOpts {
  /**
   * Whether or not to add the project id header to requests. Used in Sinch Engage to decide what project the user is interacting with.
   */
  projectIdHeader: boolean;
  /**
   * Whether or not to initialize Google Tag Manager, configured for Sinch Engage
   */
  googleTagManager: boolean;
  /**
   * Whether or not to initialize Rudder Stack
   */
  rudderstack: boolean;
  /**
   * Whether or not to initialize Hotjar
   */
  hotjar: boolean;
  /**
   * Whether or not to initialize exception monitoring with datadog
   */
  exceptionMonitoring: boolean;
  /**
   * Whether or not to initialize the MessageMedia themes
   */
  messageMediaThemes: boolean;
  /**
   * Whether or not to patch requests with auth headers
   */
  patchRequestsWithAuth: boolean;
  /**
   * Whether or not to require critical browser support. If the browser does not support the critical features, the user will be redirected to a page explaining the issue.
   */
  requireCriticalBrowserSupport: boolean;
  /**
   * Whether or not to set the browser language as a default
   */
  setDefaultLanguageToBrowserDefault: boolean;

  /**
   * An optional extra nectary theme to be installed
   */
  extraNectaryTheme: string | null;

  /**
   * Whether or not to render the app without a shadow root
   */
  withoutShadowRoot: boolean;
  /**
   * Whether utm params should be handled
   */
  handleUtmParameters: boolean;
  /**
   * Values that should be added to the window object.
   */
  extraWindowValues: { key: string; value: unknown }[];
}

function patchHistory() {
  // Fix for browser history routing where each MFE has its own router and
  // internal routing state. This hack fakes a popstate event when a router calls
  // `pushState` or `replaceState` so that all routers are updated.
  const { pushState, replaceState } = history;

  history.pushState = function (...args) {
    pushState.call(this, ...args);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  history.replaceState = function (...args) {
    replaceState.call(this, ...args);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
}

async function setupExceptionMonitoring() {
  const { initExceptionMonitoring } = await import("./exceptionMonitoring");
  const missingCriticalBrowserSupport = isBrowserLackingCriticalFunctionality();
  const missingBrowserVersionSupport = isBrowserOld();
  initExceptionMonitoring({
    missingCriticalBrowserSupport,
    missingBrowserVersionSupport,
  });
}

const removeCacheBustingParam = () => {
  const params = new URLSearchParams(location.search);
  if (params.has("t")) {
    params.delete("t");
    window.history.replaceState(
      {},
      "",
      `${location.pathname}${params.size > 0 ? `?${params.toString()}` : ""}`
    );
  }
};

type Step = (cfg: Config) => Promise<void>;

function runSteps(steps: Step[], cfg: Config): Promise<void> {
  return Promise.all(steps.map((step) => step(cfg))).then(() => {
    void 0;
  });
}

/**
 * A tranche is a series of steps that run in parallel.
 */
type Tranche = Step[];

/**
 * Runs a series of tranches, where each tranche runs in series, but the contents of each tranche run in parallel.
 * @param tranches the tranches, which finish one after the other, the contents of each tranche run in parallel
 * @param opts
 */
async function runTranches(tranches: Tranche[], cfg: Config): Promise<void> {
  for (const tranche of tranches) {
    await runSteps(tranche, cfg);
  }
}

function conditionalStep(
  criteria: (cfg: Config) => boolean,
  step: Step,
  alternativeStep?: Step
): Step {
  return async (cfg) => {
    if (criteria(cfg)) {
      await step(cfg);
    } else if (alternativeStep) {
      await alternativeStep(cfg);
    }
  };
}

/**
 * Turns a step into a background task that doesn't need to terminate for rendering to progress.
 */
function backgroundStep(s: Step): Step {
  return async (opts) => {
    setTimeout(() => {
      s(opts);
    }, 0);
  };
}

const assignDefaultFeatureFlags = async () => {
  const { setGlobalConfigFromStorage } = await import("./config/feature-flag");
  return setGlobalConfigFromStorage();
};

const setupFeatureFlags = async () => {
  const { CONFIGCAT_SDK_KEY: featureFlagClientKey } = window.SHELL_ENV;

  const { assignFeatureFlags, initFeatureFlags } = await import(
    "./config/config-cat-client"
  );

  const { getFeatureFlagSessionContract } = await import("./featureFlags");
  await initFeatureFlags({
    featureFlagClientKey,
  });
  await assignFeatureFlags(getFeatureFlagSessionContract());
};

const setupFeatureFlagsStep = conditionalStep(
  // Check if there is a config stored in the localStorage
  () => hasStoredFlags(),
  // If they are, we can use them while fetching the config
  () => (assignDefaultFeatureFlags(), setupFeatureFlags()),
  // If not we need to await fetching config
  async () => await setupFeatureFlags()
);

const requireCriticalBrowserSupport = conditionalStep(
  ({ universalBootstrapOptions: { requireCriticalBrowserSupport } }) =>
    requireCriticalBrowserSupport,
  async () => {
    if (isBrowserLackingCriticalFunctionality()) {
      history.pushState("", "", "/missing-browser-support");
    }
  }
);

const setFaviconAndTitleStep: Step = async () => {
  const { setFaviconAndTitle } = await import("./setFaviconAndTitle");
  setFaviconAndTitle();
};

const busPlatformHandlerStep: Step = async () => {
  import("./bus-platform-handler");
};

const busTokenHandlerStep: Step = async () => {
  import("./bus-token-handler");
};

const setupAuthStep: Step = async ({
  universalBootstrapOptions: { patchRequestsWithAuth },
}) => {
  const { setupAuth } = await import("./auth");
  setupAuth(patchRequestsWithAuth);
};

const globalJotaiStoreStep: Step = async ({
  universalBootstrapOptions: { handleUtmParameters },
}) => {
  const { globalJotaiStore } = await import("./jotai-store");
  const { syncLocation } = await import("./components/Router/state");
  syncLocation(globalJotaiStore);
  if (handleUtmParameters) {
    const { storeUtmParametersInCookie } = await import(
      "./handleUtmParameters"
    );

    storeUtmParametersInCookie(globalJotaiStore);
  }
};

const mfeLoaderStep: Step = async () => {
  const { setupMFELoaders } = await import("./mfe/mfeLoaders");
  const { ENV } = await import("./helpers/getEnv");

  setupMFELoaders(ENV);
  import(/* webpackPreload: true */ "./mfe/bus-mfe-config-handler");
};

const setVendorLabelStep = conditionalStep(
  ({ universalBootstrapOptions: { messageMediaThemes } }) =>
    !messageMediaThemes,
  async () => {
    const { setVendorLabel } = await import("./portals/messagemedia/theme");
    await setVendorLabel();
  }
);

const messageMediaThemesStep = conditionalStep(
  ({ universalBootstrapOptions: { messageMediaThemes } }) => messageMediaThemes,
  async () => {
    const { setupMessageMediaThemes, setVendorLabel } = await import(
      "./portals/messagemedia/theme"
    );
    await setVendorLabel();
    await setupMessageMediaThemes();
  }
);

const cacheLayerStep = async () => {
  const { patchFetchWithCacheLayer } = await import("./patchFetchWithCache");
  patchFetchWithCacheLayer();
};

const projectIdHeaderStep = conditionalStep(
  ({ universalBootstrapOptions: { projectIdHeader } }) => projectIdHeader,
  async () => {
    const { enableProjectIdHeader } = await import("./x-project-id");
    enableProjectIdHeader();
  }
);

const googleTagManagerStep = conditionalStep(
  ({ universalBootstrapOptions: { googleTagManager } }) => googleTagManager,
  async () => {
    const { initGoogleTagManager } = await import(
      "./analytics/googleTagManager"
    );
    initGoogleTagManager();
  }
);

const rudderstackStep = backgroundStep(
  conditionalStep(
    ({ universalBootstrapOptions: { rudderstack } }) => rudderstack,
    async () => {
      const { initRudderstack } = await import("./analytics/rudderstack");
      initRudderstack();
    }
  )
);

const hotjarStep = backgroundStep(
  conditionalStep(
    ({ universalBootstrapOptions: { hotjar } }) => hotjar,
    async () => {
      const { initHotjar } = await import("./analytics/hotjar");
      initHotjar();
    }
  )
);

const exceptionMonitoringStep = backgroundStep(
  conditionalStep(
    ({ universalBootstrapOptions: { exceptionMonitoring } }) =>
      exceptionMonitoring,
    async () => {
      await setupExceptionMonitoring();
    }
  )
);

const setDefaultLanguageToBrowserDefaultStep = conditionalStep(
  ({ universalBootstrapOptions: { setDefaultLanguageToBrowserDefault } }) =>
    setDefaultLanguageToBrowserDefault,
  async () => {
    const { setWindowIntlMeta, getBrowserLang } = await import(
      "./intl/intl.utils"
    );
    setWindowIntlMeta(getBrowserLang());
  }
);

const addWindowValues: Step = async (config) => {
  const { extraWindowValues } = config.universalBootstrapOptions;
  if (extraWindowValues) {
    extraWindowValues.forEach((kv) => {
      Object.assign(window, { [kv.key]: kv.value });
    });
  }
};

const UNIVERSAL_BOOTSTRAP_STEPS: Step[] = [
  requireCriticalBrowserSupport,
  setFaviconAndTitleStep,
  busPlatformHandlerStep,
  busTokenHandlerStep,
  setupAuthStep,
  globalJotaiStoreStep,
  mfeLoaderStep,
  projectIdHeaderStep,
  googleTagManagerStep,
  rudderstackStep,
  hotjarStep,
  exceptionMonitoringStep,
  setDefaultLanguageToBrowserDefaultStep,
  addWindowValues,
];

const BOOTSTRAP_TRANCHES = [
  [setupFeatureFlagsStep],
  [async () => removeCacheBustingParam()],
  [async () => patchHistory()],
  [cacheLayerStep],
  [setVendorLabelStep, messageMediaThemesStep],
  UNIVERSAL_BOOTSTRAP_STEPS,
];

export async function universalBootstrap(config: Config): Promise<void> {
  await runTranches(BOOTSTRAP_TRANCHES, config);
}
